import LandingLayout from '../../components/layout/LandingLayout';

export default function BAAPage() {
  return (
    <LandingLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-extrabold text-neutral-900 mb-2">Business Associate Agreement</h1>
        <p className="text-sm text-neutral-500 mb-10">Last updated: April 24, 2026</p>

        <div className="bg-primary-50 border border-primary-100 rounded-xl px-6 py-4 mb-10 text-sm text-primary-800">
          This Business Associate Agreement (&quot;BAA&quot;) is entered into between Sage Mental Inc. (&quot;Business
          Associate&quot;) and the covered entity or business associate that has accepted these terms
          (&quot;Covered Entity&quot;). It is incorporated into and governed by the Sage Terms of Service.
          By using the Sage platform, you agree to the terms of this BAA.
        </div>

        <div className="prose prose-neutral max-w-none space-y-8 text-neutral-700 leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">1. Definitions</h2>
            <p className="mb-3">
              Terms used in this BAA have the meanings defined in HIPAA (45 C.F.R. Parts 160 and 164),
              including:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>PHI</strong> — Protected Health Information as defined at 45 C.F.R. § 160.103</li>
              <li><strong>ePHI</strong> — Electronic PHI</li>
              <li><strong>HIPAA Rules</strong> — The Privacy Rule, Security Rule, Breach Notification Rule, and Enforcement Rule</li>
              <li><strong>Services</strong> — The Sage practice management platform provided under the Terms of Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">2. Permitted uses and disclosures</h2>
            <p className="mb-3">
              Sage Mental Inc. may use and disclose PHI only to:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Provide the Services described in the Terms of Service</li>
              <li>Perform data aggregation services for the Covered Entity&apos;s health care operations</li>
              <li>Perform its own proper management and administration activities</li>
              <li>Carry out its legal responsibilities, as permitted by law</li>
            </ul>
            <p className="mt-3">
              Sage Mental Inc. will not use or disclose PHI for marketing, research, or any purpose not
              expressly described in this BAA or required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">3. Safeguards</h2>
            <p>
              Sage Mental Inc. will implement and maintain appropriate administrative, physical, and
              technical safeguards to protect the confidentiality, integrity, and availability of ePHI
              in accordance with the HIPAA Security Rule (45 C.F.R. Part 164, Subpart C). This includes
              encryption in transit and at rest, access controls, audit logging, and workforce training.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">4. Subcontractors</h2>
            <p>
              Sage Mental Inc. will enter into a written agreement with any subcontractor that creates,
              receives, maintains, or transmits PHI on behalf of Sage Mental Inc., requiring the
              subcontractor to comply with the same conditions and restrictions that apply to Sage Mental
              Inc. under this BAA.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">5. Reporting</h2>
            <p>
              Sage Mental Inc. will report to the Covered Entity without unreasonable delay, and in no
              case later than 60 days from discovery, any use or disclosure of PHI not permitted by this
              BAA, any Security Incident of which it becomes aware, and any Breach of Unsecured PHI
              as defined in 45 C.F.R. § 164.402.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">6. Individual rights</h2>
            <p className="mb-3">
              To the extent Sage Mental Inc. maintains PHI in a Designated Record Set, Sage Mental Inc.
              will make such PHI available to the Covered Entity to fulfill individual access requests
              under 45 C.F.R. § 164.524, and will amend PHI as directed by the Covered Entity under
              45 C.F.R. § 164.526.
            </p>
            <p>
              Sage Mental Inc. will make available to the Covered Entity information required to provide
              an accounting of disclosures in accordance with 45 C.F.R. § 164.528.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">7. Access for government agencies</h2>
            <p>
              Sage Mental Inc. will make its internal practices, books, and records relating to the
              use and disclosure of PHI available to the Secretary of the U.S. Department of Health and
              Human Services for purposes of determining compliance with the HIPAA Rules.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">8. Obligations of the Covered Entity</h2>
            <p className="mb-3">The Covered Entity agrees to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Notify Sage Mental Inc. of any restrictions on use or disclosure of PHI required under 45 C.F.R. § 164.522</li>
              <li>Not request Sage Mental Inc. to use or disclose PHI in a manner that would violate the HIPAA Rules</li>
              <li>Obtain any consents, authorizations, or other permissions necessary to allow Sage Mental Inc. to perform the Services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">9. Term and termination</h2>
            <p className="mb-3">
              This BAA is effective upon acceptance and continues until termination of the Terms of
              Service. Either party may terminate this BAA if the other party materially breaches a
              provision and fails to cure within 30 days of written notice.
            </p>
            <p>
              Upon termination, Sage Mental Inc. will return or destroy all PHI received from or on
              behalf of the Covered Entity, if feasible. If return or destruction is not feasible, Sage
              Mental Inc. will extend the protections of this BAA to the PHI and limit further use or
              disclosure to those purposes that make the return or destruction infeasible.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">10. Miscellaneous</h2>
            <p>
              This BAA shall be interpreted as broadly as necessary to implement and comply with the
              HIPAA Rules. Any ambiguity shall be resolved in favor of a meaning that permits the
              Covered Entity to comply with the HIPAA Rules. This BAA supersedes any prior BAA between
              the parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Contact</h2>
            <p>
              For BAA-related inquiries:<br />
              Sage Mental Inc. — Privacy Officer<br />
              <a href="mailto:privacy@sagemental.net" className="text-primary-600 hover:underline">privacy@sagemental.net</a>
            </p>
          </section>

        </div>
      </div>
    </LandingLayout>
  );
}
